import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { SearchIndexRow } from "@/features/sparxsEditor/lib/advancedSearch/types/searchTypes";
import { sparxsEditorCatalogReplaced } from "@/features/sparxsEditor/store/domainActions";

export interface SparxsSearchIndexState {
  rowsByFileName: Record<string, SearchIndexRow>;

  totalFiles: number;

  completedFiles: number;
  isIndexing: boolean;
}

const initialState: SparxsSearchIndexState = {
  rowsByFileName: {},
  totalFiles: 0,
  completedFiles: 0,
  isIndexing: false,
};

const sparxsSearchIndexSlice = createSlice({
  name: "sparxsSearchIndex",
  initialState,
  reducers: {
    backgroundIndexStarted: (
      state,
      action: PayloadAction<{ totalFiles: number; clearRows?: boolean }>,
    ) => {
      const clearRows = action.payload.clearRows !== false;
      state.isIndexing = true;
      state.totalFiles = action.payload.totalFiles;
      state.completedFiles = 0;
      if (clearRows) {
        state.rowsByFileName = {};
      }
    },
    backgroundIndexFinished: (state) => {
      state.isIndexing = false;
      state.totalFiles = 0;
      state.completedFiles = 0;
    },
    indexRunProgressed: (state) => {
      if (state.isIndexing && state.completedFiles < state.totalFiles) {
        state.completedFiles += 1;
      }
    },
    ingestSearchIndexRow: (state, action: PayloadAction<SearchIndexRow>) => {
      const row = action.payload;
      state.rowsByFileName[row.fileName] = row;
    },
    pruneSearchIndexRows: (
      state,
      action: PayloadAction<{ keepFileNames: string[] }>,
    ) => {
      const keep = new Set(action.payload.keepFileNames);
      const next: Record<string, SearchIndexRow> = {};
      for (const name of keep) {
        const row = state.rowsByFileName[name];
        if (row != null) {
          next[name] = row;
        }
      }
      state.rowsByFileName = next;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sparxsEditorCatalogReplaced, (state, action) => {
      if (action.payload.files.length > 0) {
        return;
      }
      state.rowsByFileName = {};
    });
  },
});

export const {
  backgroundIndexFinished,
  backgroundIndexStarted,
  indexRunProgressed,
  ingestSearchIndexRow,
  pruneSearchIndexRows,
} = sparxsSearchIndexSlice.actions;

export default sparxsSearchIndexSlice.reducer;
